import { TooltipPlacement } from "@app/shared/literals";
import Portal from "@app/shared/Portal";
import { commonFocusableElements } from "@app/shared/utility/commonFocusableElements";
import classnames from "classnames";
import { clamp, uniqueId } from "lodash";
import React, { FC, JSX, ReactNode, useEffect, useMemo, useRef, useState } from "react";

interface TooltipProps {
  children?: ReactNode;
  containerClassName?: string;
  /**
   * This is to be used if the tooltip is inside a flyout. It should be given the flyout's id,
   * so that clicking inside the tooltip does not close the flyout.
   */
  containerId?: string;
  delay?: number;
  position?: TooltipPlacement;
  style?: React.CSSProperties;
  text: string | JSX.Element;
  /**
   * Extra class applied to the portaled `.tooltip-body` div (not the trigger wrapper).
   * Use when the tooltip sits inside a stacking context above the default z-index of 1120
   * — e.g., inside a modal — so you can override z-index without touching the shared style.
   */
  tooltipBodyClassName?: string;
  triggerWidth?: string;
  tooltipWidth?: string;
  sticky?: boolean;
  oneLine?: boolean;
  disableOnFocus?: boolean;
  suppressTriggerTabIndex?: boolean;
}

interface Coords {
  x: number;
  y: number;
}

interface TooltipPositionInfo {
  // Where the arrow sits along the tooltip's edge, in px from its left (or top) corner.
  arrowOffset: number;
  coords: Coords;
  position: TooltipPlacement;
}

const TRIGGER_GAP = 16;
const VIEWPORT_MARGIN = 8;
const ARROW_INSET = 12;
const NO_OFFSET = { x: 0, y: 0 };

const getViewportHeight = () => document.documentElement.clientHeight || window.innerHeight;
const getViewportWidth = () => document.documentElement.clientWidth || window.innerWidth;

// These track the currently visible tooltip so that opening a new one
// immediately closes the previous one, regardless of mouseover/mouseleave ordering.
let activeTooltipClose: (() => void) | null = null;
let activeTooltipId: string | null = null;

const TooltipWrapper: FC<React.PropsWithChildren<TooltipProps>> = (props: TooltipProps) => {
  const showTimeout = useRef<number>(null);
  const unRenderTimeout = useRef<number>(null);
  const isMousedOver = useRef(false);
  const isFocused = useRef(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipId = useRef(uniqueId());
  const container = useRef(document.getElementById(props.containerId)).current;
  const triggerRect = useRef<DOMRect>(null);
  const [arrowOffset, setArrowOffset] = useState<number>(null);
  const [coordinates, setCoordinates] = useState<Coords>({ x: 0, y: 0 });
  const [resolvedPosition, setResolvedPosition] = useState<TooltipPlacement>(props.position);
  const [shouldRender, setShouldRender] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [shouldHaveTabIndex, setShouldHaveTabIndex] = useState(true);

  const containerClass = useMemo(() => {
    return !!props.containerClassName ? `${props.containerClassName} tooltip-container` : "tooltip-container";
  }, [props.containerClassName]);

  useEffect(() => {
    if (shouldRender && !!tooltipRef.current) {
      showTimeout.current = window.setTimeout(() => {
        const placement = getRenderedPlacement(tooltipRef.current);
        if (placement) {
          setArrowOffset(placement.arrowOffset);
          setCoordinates(placement.coords);
          setResolvedPosition(placement.position);
          setShouldShow(true);
        }
      }, 100 + (props.delay || 0));
    }
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldShow && !!tooltipRef.current) {
      window.setTimeout(() => {
        setShouldRender(false);
      }, 300); // allows time for the css transition
    }
  }, [shouldShow]);

  useEffect(() => {
    const event = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        unRenderTooltip();
        if (isFocused.current) {
          (document.activeElement as HTMLElement).blur();
          isFocused.current = false;
        }
        window.removeEventListener("keyup", event);
      }
    };
    if (shouldShow) {
      // for some reason "Escape" doesn't trigger keypress
      window.addEventListener("keyup", event);
    }

    return () => {
      window.removeEventListener("keyup", event);
    }
  }, [shouldShow]);

  // On unmount, clear the module-level active tooltip references if this instance owns them.
  // Without this, activeTooltipClose would hold a stale closure over a dead component (causing
  // React warnings on state updates), and activeTooltipId would prevent the next tooltip from
  // correctly recognizing that no tooltip is currently active.
  useEffect(() => {
    return () => {
      if (activeTooltipId === tooltipId.current) {
        activeTooltipId = null;
        activeTooltipClose = null;
      }
    };
  }, []);

  // This will remove an unnecessary tabindex attribute from the 
  // tooltip trigger span if props.children includes buttons, a, inputs, etc.
  // We have to rerun it on children updates because they _can_ change,
  // e.g. changing the disabled attribute on a button
  useEffect(() => {
    setShouldHaveTabIndex(!triggerRef.current?.querySelector(commonFocusableElements));
  }, [props.children]);

  // Keeps the tooltip on screen. `coords` is the css left/top about to be set and `offset` is
  // what the placement's transform then adds, so the painted box starts at coords + offset.
  const clampToViewport = (coords: Coords, offset: Coords, tRect: DOMRect): Coords => ({
    x: clamp(
      coords.x + offset.x,
      window.scrollX + VIEWPORT_MARGIN,
      window.scrollX + getViewportWidth() - tRect.width - VIEWPORT_MARGIN,
    ) - offset.x,
    y: clamp(
      coords.y + offset.y,
      window.scrollY + VIEWPORT_MARGIN,
      window.scrollY + getViewportHeight() - tRect.height - VIEWPORT_MARGIN,
    ) - offset.y,
  });

  // Chooses above vs. below the trigger
  const getVerticalPlacement = (tRect: DOMRect): TooltipPositionInfo => {
    if (!triggerRect.current) {
      return { arrowOffset: null, coords: coordinates, position: props.position };
    }

    const wantsBottom = props.position === "bottom";
    const roomAbove = triggerRect.current.top - VIEWPORT_MARGIN;
    const roomBelow = getViewportHeight() - triggerRect.current.bottom - VIEWPORT_MARGIN;
    const requestedRoom = wantsBottom ? roomBelow : roomAbove;
    const oppositeRoom = wantsBottom ? roomAbove : roomBelow;
    const flipped = requestedRoom < tRect.height + TRIGGER_GAP && oppositeRoom > requestedRoom;
    const showBelow = wantsBottom !== flipped;

    const coords = {
      x: coordinates.x,
      y: showBelow
        ? triggerRect.current.bottom + window.scrollY + TRIGGER_GAP
        : triggerRect.current.top + window.scrollY - TRIGGER_GAP,
    };
    // Both sides are centred on the trigger; only "top" pulls the box up by its own height.
    const offset = { x: -tRect.width / 2, y: showBelow ? 0 : -tRect.height };
    const clamped = clampToViewport(coords, offset, tRect);

    return {
      // Keeps the arrow on the trigger even when the clamp above has repositioned the tooltip.
      arrowOffset: clamp(coordinates.x - (clamped.x + offset.x), ARROW_INSET, tRect.width - ARROW_INSET),
      coords: clamped,
      position: flipped ? (showBelow ? "bottom" : "top") : props.position,
    };
  }

  const getHorizontalPlacement = (x: number, tRect: DOMRect): TooltipPositionInfo => {
    const clamped = clampToViewport({ x, y: coordinates.y - tRect.height / 2 }, NO_OFFSET, tRect);
    return {
      arrowOffset: clamp(coordinates.y - clamped.y, ARROW_INSET, tRect.height - ARROW_INSET),
      coords: clamped,
      position: props.position,
    };
  }

  const getRenderedPlacement = (el: Element) => {
    if (!el) { return; } // in case the element has unmounted before the timeout called this
    const tRect = el.getBoundingClientRect();
    switch (props.position) {
      case "left":
        return getHorizontalPlacement(coordinates.x - tRect.width, tRect);
      case "right":
        return getHorizontalPlacement(coordinates.x, tRect);
      case "top":
      case "bottom":
      default:
        return getVerticalPlacement(tRect);
    }
  }

  const handleMouseOver = (el: Element) => {
    window.clearTimeout(unRenderTimeout.current);
    unRenderTimeout.current = null;
    const rect = (el).closest(".tooltip-trigger")?.getBoundingClientRect();
    if (!rect) {
      unRenderTooltip();
      return;
    }

    // This will close any other visible tooltip immediately before showing the new one.
    // This must happen after the rect check so we don't end up closing another tooltip
    // when the mouse passes over a non-trigger child element.
    if (activeTooltipClose && activeTooltipId !== tooltipId.current) {
      activeTooltipClose();
      activeTooltipClose = null;
      activeTooltipId = null;
    }

    isMousedOver.current = true;
    if (showTimeout.current != null) {
      return;
    }
    renderTooltip(rect);
  }

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    if (props.disableOnFocus) {
      return;
    }

    const rect = (e.target as Element).closest(".tooltip-trigger")?.getBoundingClientRect();
    if (!rect) {
      unRenderTooltip();
      return;
    }
    isFocused.current = true;
    if (showTimeout.current != null) {
      return;
    }
    renderTooltip(rect);
  }

  const handleMouseLeave = () => {
    isMousedOver.current = false;
    if (isFocused.current) {
      return;
    }
    unRenderTimeout.current = window.setTimeout(() => {
      unRenderTooltip();
    }, 250);
  }

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    isFocused.current = false;
    if (isMousedOver.current || e.relatedTarget?.classList.contains("tooltip-body")) {
      return;
    }
    unRenderTooltip();
  }

  const renderTooltip = (rect: DOMRect) => {
    // Register this tooltip as the active one so 
    // that any subsequent tooltip can close it
    activeTooltipId = tooltipId.current;
    activeTooltipClose = () => {
      window.clearTimeout(showTimeout.current);
      showTimeout.current = null;
      window.clearTimeout(unRenderTimeout.current);
      unRenderTimeout.current = null;
      setShouldShow(false);
      setShouldRender(false);
    };

    // Start from the requested side and flips it once the tooltip is measured.
    triggerRect.current = rect;
    setResolvedPosition(props.position);

    switch (props.position) {
      case "left":
        // we also subtract the tooltip's width and height in a function above
        setCoordinates({
          x: rect.left + window.scrollX - 16,
          y: rect.top + window.scrollY + (rect.height / 2),
        });
        setShouldRender(true);
        break;
      case "right":
        // we also subtract the tooltip's height in a function above
        setCoordinates({
          x: rect.right + window.scrollX + 16,
          y: rect.top + window.scrollY + (rect.height / 2),
        });
        setShouldRender(true);
        break;
      case "bottom":
        setCoordinates({
          x: rect.left + window.scrollX + (rect.width / 2),
          y: rect.bottom + window.scrollY + 16,
        });
        setShouldRender(true);
        break;
      case "top":
      default:
        setCoordinates({
          x: rect.left + window.scrollX + (rect.width / 2),
          y: rect.top + window.scrollY - 16,
        });
        setShouldRender(true);
        break;
    }
  }

  const unRenderTooltip = () => {
    // Unregister the current tooltip as the active tooltip
    if (activeTooltipId === tooltipId.current) {
      activeTooltipClose = null;
      activeTooltipId = null;
    }

    if (shouldShow) {
      if (showTimeout.current != null) {
        window.clearTimeout(showTimeout.current);
        showTimeout.current = null;
      }
      // only set show to false so we can transition out
      setShouldShow(false);
    } else {
      if (showTimeout.current != null) {
        if (props.sticky)
          return;

        window.clearTimeout(showTimeout.current);
        showTimeout.current = null;
      }
      // mouseLeave must have triggered early
      setShouldRender(false);
      setShouldShow(false);
    }
  }

  return (
    !props.text ? <>{props.children}</> :
      <span className={containerClass} style={props.style}>
        {shouldRender && !!props.text ?
          (<Portal hideBodyScroll={false} container={container}>
            <div
              className={classnames(
                "tooltip-body",
                "tooltip-portal",
                props.tooltipBodyClassName,
                {
                  [resolvedPosition]: !!resolvedPosition,
                  show: shouldShow,
                },
              )}
              ref={tooltipRef}
              style={{
                display: "block",
                left: `${coordinates.x}px`,
                maxWidth: props.oneLine ? undefined : `${getViewportWidth() - (VIEWPORT_MARGIN * 2)}px`,
                top: `${coordinates.y}px`,
                whiteSpace: props.oneLine ? "nowrap" : "normal",
                width: props.tooltipWidth || "auto",
              }}
              id={tooltipId.current}
              aria-hidden={!shouldShow}
              onMouseOver={() => handleMouseOver(triggerRef.current)}
              onMouseLeave={handleMouseLeave}
            >
              {props.text}
              <div
                className="tooltip-arrow"
                style={arrowOffset == null ? undefined
                  : resolvedPosition === "left" || resolvedPosition === "right"
                    ? { top: `${arrowOffset}px` }
                    : { left: `${arrowOffset}px` }}
              />
            </div>
          </Portal>)
          : null
        }
        <span
          className="tooltip-trigger"
          ref={triggerRef}
          onBlur={handleBlur}
          onFocus={handleFocus} // focus and blur allow keyboard control to show/hide the tooltip
          onMouseLeave={handleMouseLeave}
          onMouseOver={(e) => {
            e.stopPropagation();
            handleMouseOver(e.target as HTMLElement);
          }}
          style={{ width: props.triggerWidth || "auto" }}
          aria-describedby={tooltipId.current}
          // makes the element keyboard-focusable in a default order
          // accessibility recommendations say tooltips should only really be on interactive elements like buttons, 
          // but we have a ton that just describe truncated words and things like that, and those elements
          // aren't focusable by default
          // However, if the child _is_ interactive, we don't want it to get tab focus because then the trigger and the
          // child will both show up in tab order. We can't know what HTML elements are contained in props.children,
          // so we have a useEffect set up to look at it after render. As a result, we want buttons and links to be
          // inside TooltipWrapper, and not vice versa.
          // suppressTriggerTabIndex is the escape hatch for when the wrapper is deliberately placed _inside_ an
          // already-focusable ancestor (e.g. a button), so the trigger must not add its own second tab stop.
          tabIndex={shouldHaveTabIndex && !props.suppressTriggerTabIndex ? 0 : undefined}
        >
          {props.children}
        </span>
      </span>
  );
}

export default TooltipWrapper;